import { Button, type ButtonProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import type { FC, PropsWithChildren, SyntheticEvent } from 'react';
import { useRef } from 'react';

import { FileIcon, UploadIcon } from '../icon/Icons';

const useStyles = makeStyles(() => ({
  fileInput: {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  },

  actionButtonLabel: {
    display: 'block',
    textTransform: 'none',
    marginLeft: '10px',
    fontStyle: 'italic',
  },

  actionButtonBigLabel: {
    fontWeight: 'bold',
    fontStyle: 'normal',
  },

  fileName: {
    fontStyle: 'italic',
    fontWeight: 'normal',
    textAlign: 'start',
  },

  fileIcon: {
    transform: 'rotate(-90deg)',
  },
}));

interface Props extends Pick<ButtonProps, 'variant' | 'color' | 'fullWidth'> {
  onFileChange: (e: SyntheticEvent<HTMLInputElement>) => void;
  fileName?: string;
  bigger?: boolean;
}

export const UploadButton: FC<PropsWithChildren<Props>> = ({
  onFileChange,
  fileName,
  bigger,
  ...otherProps
}) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onOpenFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const icon = fileName ? <FileIcon /> : <UploadIcon />;

  return (
    <Button
      onClick={onOpenFileDialog}
      startIcon={icon}
      size={bigger ? 'large' : 'medium'}
      {...otherProps}
    >
      <input
        className={classes.fileInput}
        type="file"
        ref={inputRef}
        onChange={onFileChange}
      />
      <div
        className={classNames(classes.actionButtonLabel, {
          [classes.actionButtonBigLabel]: bigger,
        })}
      >
        {fileName ? (
          <>
            File attachment
            <div className={classes.fileName}>{fileName}</div>
          </>
        ) : (
          'Choose a file'
        )}
      </div>
    </Button>
  );
};
